/**
 * Shared retry-with-backoff primitive for the API layer (issue #2096).
 *
 * Promoted out of chat's `httpClient` so retry is a first-class feature of the
 * shared request layer instead of a chat-private copy: the typed `api` wrapper
 * offers an opt-in `retry` option built on {@link withRetry}, and chat's
 * cancellation-aware client reuses the exact same backoff algorithm and default
 * policy rather than duplicating it.
 *
 * This module is deliberately Pinia-free (it only touches axios), so the
 * `@/core/api` barrel can re-export it without pulling in the notification
 * store (issue #1979).
 */

import axios, { type AxiosError } from "axios";

export interface RetryConfig {
  /** Maximum number of retry attempts after the first try (default: 3). */
  maxRetries: number;
  /** Initial delay between retries in milliseconds (default: 1000ms). */
  baseDelay: number;
  /** Maximum delay between retries in milliseconds (default: 30000ms). */
  maxDelay: number;
  /**
   * Whether a given error should be retried. Defaults to
   * {@link isRetryableTransportError}. A `retryCondition` of `undefined` means
   * "never retry".
   */
  retryCondition?: (error: unknown) => boolean;
}

/**
 * The shared retry policy: retry network failures, connection-abort timeouts,
 * and 5xx server errors, but never a deliberate cancel/abort (those are the
 * caller tearing the request down on purpose). Non-axios errors have no
 * `response`, so they fall through the first clause and are retried.
 */
export function isRetryableTransportError(error: unknown): boolean {
  if (axios.isCancel(error)) {
    return false;
  }
  const axiosError = axios.isAxiosError(error) ? (error as AxiosError) : undefined;
  return (
    !axiosError?.response ||
    axiosError.code === "ECONNABORTED" ||
    (axiosError.response.status >= 500 && axiosError.response.status < 600)
  );
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 30000,
  retryCondition: isRetryableTransportError,
};

/** Merge a partial override onto the shared default policy. */
export function resolveRetryConfig(override?: Partial<RetryConfig>): RetryConfig {
  return { ...DEFAULT_RETRY_CONFIG, ...override };
}

/** Exponential backoff delay for a zero-based attempt index, capped at `maxDelay`. */
export function backoffDelayMs(attempt: number, config: RetryConfig): number {
  return Math.min(config.baseDelay * Math.pow(2, attempt), config.maxDelay);
}

export interface WithRetryHooks {
  /** Called before each attempt with its zero-based index. */
  onAttempt?: (attempt: number) => void;
  /**
   * Called after a failure that will be retried, before the backoff wait.
   * `nextAttempt` is the 1-based number of the retry about to be scheduled.
   */
  onRetry?: (nextAttempt: number, delayMs: number, error: unknown) => void;
}

/**
 * Run `operation` with exponential-backoff retries. The operation receives the
 * zero-based attempt index. Failures are retried while the attempt budget
 * remains and `config.retryCondition` accepts the error; otherwise the last
 * error is rethrown unchanged (normalization is the caller's job).
 */
export async function withRetry<T>(
  operation: (attempt: number) => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  hooks: WithRetryHooks = {},
): Promise<T> {
  const run = async (attempt: number): Promise<T> => {
    hooks.onAttempt?.(attempt);
    try {
      return await operation(attempt);
    } catch (error) {
      const canRetry = attempt < config.maxRetries && (config.retryCondition?.(error) ?? false);
      if (!canRetry) {
        throw error;
      }
      const delayMs = backoffDelayMs(attempt, config);
      hooks.onRetry?.(attempt + 1, delayMs, error);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      return run(attempt + 1);
    }
  };

  return run(0);
}
